import { useSession } from "@core/hooks/useSession";
import { api } from "@core/services/nocd-api";
import { AxiosError } from "axios";
import { useQuery, UseQueryResult } from "react-query";
import slugify from "slugify";

export const getQueryKey = (accessToken?: string): [string, string] => [
  "subtypes",
  accessToken ?? "public",
];

export interface Subtype {
  id: string;
  description: string;
  imageUrl: string;
  title: string;
  selected: boolean;
  isSelected: boolean;
  slug: string;
}

export interface SubtypeDTO {
  id: string;
  title: string;
  description: string;
  selected: boolean;
  image_url: string;
}

export const transformSubtypeDto = ({
  id,
  title,
  description,
  selected,
  image_url,
}: SubtypeDTO): Subtype => {
  return {
    id,
    title,
    description,
    selected: !!selected,
    isSelected: !!selected,
    imageUrl: image_url,
    slug: slugify(title, { lower: true, strict: true }),
  };
};

export const fetchSubtypes = (
  accessToken?: string,
  signal?: AbortSignal
): Promise<Subtype[]> => {
  return api
    .get<SubtypeDTO[]>(
      "/v1/community/subtypes",
      accessToken
        ? {
            headers: { Authorization: accessToken },
            signal,
          }
        : { signal }
    )
    .then(({ data }) => data.map((subtype) => transformSubtypeDto(subtype)));
};

export const useSubtypes = (): UseQueryResult<Subtype[], AxiosError> => {
  const { data: session } = useSession();
  const accessToken = session?.accessToken;

  return useQuery(getQueryKey(accessToken), async ({ signal }) =>
    fetchSubtypes(accessToken, signal)
  );
};

type AllTopicsDto = Pick<SubtypeDTO, "id" | "title">;
type AllTopicsResponse = Pick<Subtype, "id" | "slug" | "title">[];

export const fetchAllTopics = (
  accessToken?: string,
  signal?: AbortSignal
): Promise<AllTopicsResponse> => {
  return api
    .get<AllTopicsDto[]>(
      "/v1/community/get_all_topics",
      accessToken
        ? {
            headers: { Authorization: accessToken },
            signal,
          }
        : { signal }
    )
    .then(({ data }) =>
      data.map(({ id, title }) => ({
        id,
        slug: slugify(title, { lower: true, strict: true }),
        title,
      }))
    );
};

export const findTopicById =
  (id: string) =>
  async (
    accessToken?: string
  ): Promise<Pick<Subtype, "id" | "slug" | "title">> => {
    const topics = await fetchAllTopics(accessToken);

    return topics.find((topic) => topic.id === id);
  };

export const useTopicById = (
  id: string
): UseQueryResult<Pick<Subtype, "id" | "slug" | "title">, AxiosError> => {
  const { data: session } = useSession();
  const accessToken = session?.accessToken;

  const queryKey = ["topic-by-id", id, accessToken];
  const queryFn = () => findTopicById(id)(accessToken);

  return useQuery(queryKey, queryFn, { enabled: !!id });
};
